/**
 * Les personnages, dans la base locale.
 *
 * Le personnage est rangé **en bloc**, tel que le moteur l'a produit. Le schéma
 * normalisé de la conception est celui du serveur ; le côté navigateur n'est
 * qu'un cache, et le découper en huit tables ici n'apporterait rien qu'un
 * assemblage à refaire à chaque ouverture.
 */

import type { Table } from 'dexie'

import { base } from './base'
import type { PersonnageEnregistre } from '../rules'

export interface FicheEnBase {
  /**
   * Un identifiant tiré sur le client, pas un compteur.
   *
   * Deux appareils hors ligne créeraient tous les deux le personnage n° 4 ;
   * deux UUID ne se rencontrent jamais. C'est ce qui rendra la synchronisation
   * possible sans renumérotation.
   */
  id: string
  nom: string
  /** ISO 8601, en UTC. */
  cree_le: string
  modifie_le: string
  /**
   * Le numéro de révision d'ADR-001 : le serveur refusera une écriture fondée
   * sur une révision périmée plutôt que d'écraser en silence.
   */
  revision: number
  /**
   * La fiche porte-t-elle des modifications que le serveur n'a pas ?
   *
   * `revision` ne peut pas répondre à cette question : elle garde le numéro que
   * le serveur a confirmé, et les modifications locales ne l'avancent pas. Sans
   * ce marqueur, la synchronisation ne distingue pas « je n'ai rien touché
   * depuis » de « j'ai des changements à envoyer » — et écrase les seconds en
   * croyant rattraper les premiers.
   *
   * Absent des fiches enregistrées avant qu'il n'existe : elles s'ouvrent alors
   * comme si tout était à envoyer, ce qui est la lecture prudente.
   */
  a_pousser?: boolean
  personnage: PersonnageEnregistre
}

const personnages = () => base.table('personnages') as Table<FicheEnBase, string>

const maintenant = () => new Date().toISOString()

export async function lister(): Promise<FicheEnBase[]> {
  const tout = await personnages().toArray()
  // Le plus récemment touché en premier : c'est celui qu'on rouvre.
  return tout.sort((a, b) => b.modifie_le.localeCompare(a.modifie_le))
}

export function lire(id: string): Promise<FicheEnBase | undefined> {
  return personnages().get(id)
}

/** Enregistre un personnage tout neuf et rend sa fiche en base. */
export async function enregistrer(personnage: PersonnageEnregistre): Promise<FicheEnBase> {
  const fiche: FicheEnBase = {
    id: crypto.randomUUID(),
    nom: personnage.nom,
    cree_le: maintenant(),
    modifie_le: maintenant(),
    revision: 1,
    // Neuve, donc jamais déposée.
    a_pousser: true,
    personnage,
  }
  await personnages().add(fiche)
  return fiche
}

/**
 * Écrit la fiche modifiée, sans toucher à la révision.
 *
 * C'est le **seul** chemin d'écriture de l'application : tout ce que l'écran
 * change — un point de vie, une Capacité apprise, un Rang franchi — passe par
 * ici.
 *
 * La révision n'avance pas, et ce n'est pas un oubli. Elle porte **le numéro
 * que le serveur a confirmé en dernier**, pas un compteur de modifications
 * locales : c'est le dépôt qui l'avance, et le serveur qui rend le suivant. Un
 * conflit signifie alors exactement *un autre appareil a déposé depuis mon
 * dernier rendez-vous* — le seul cas qui mérite qu'on arbitre. L'avancer ici
 * ferait crier au conflit à chaque point de vie perdu.
 */
export async function enregistrerCompteurs(
  id: string,
  personnage: PersonnageEnregistre,
): Promise<void> {
  const existant = await personnages().get(id)
  if (!existant) return
  await personnages().put({
    ...existant,
    modifie_le: maintenant(),
    a_pousser: true,
    personnage,
  })
}

export function supprimer(id: string): Promise<void> {
  return personnages().delete(id)
}
